/**
 * The decisions behind two browser behaviours (Jeremy, 1 Nov 2026), kept here
 * as pure functions so they are tested; the browser wiring is in
 * web/src/refreshOnReturn.ts and web/src/UpdateBar.tsx. Fieldpath has the
 * same rules in its own web/src/refreshOnReturn.ts and updateCheck.ts.
 *
 * PAGES REFRESH WHEN YOU COME BACK TO THE TAB (punch list F172): the screen on
 * show reads its data again, quietly, at most once every 15 seconds, and
 * NEVER while somebody is editing -- a pop-up with a field in it, the cursor
 * in a field, or typed text not yet saved.
 *
 * "CANOPY HAS BEEN UPDATED." (punch list F173): the server's index.html names
 * a different hashed build script from the one the page is running. Never in
 * the first minute, then every five minutes while visible and on coming back.
 * Not knowing is never "newer".
 */
/** The shortest gap between two refreshes of one page. */
export const MIN_GAP_MS = 15_000;
/** Whether enough time has passed since the last read for another. */
export function dueForRefresh(now, last, gap = MIN_GAP_MS) {
    return now - last >= gap;
}
/** Input types that are not typed into: clicked, or a filter. */
const NOT_TYPED = new Set([
    'search',
    'checkbox',
    'radio',
    'button',
    'submit',
    'reset',
    'range',
    'color',
    'file',
    'image',
    'hidden',
]);
/** A control somebody types into: a text-like input, a textarea, or editable text. */
export function isTypedField(el) {
    if (!el)
        return false;
    if (el.isContentEditable)
        return true;
    const tag = el.tagName.toUpperCase();
    if (tag === 'TEXTAREA')
        return true;
    if (tag !== 'INPUT')
        return false;
    return !NOT_TYPED.has((el.type || 'text').toLowerCase());
}
/** A field that makes a pop-up a FORM: anything typed into, or a drop-down. */
export function isFormField(el) {
    if (!el)
        return false;
    return isTypedField(el) || el.tagName.toUpperCase() === 'SELECT';
}
/** Whether somebody is in the middle of editing. */
export function isEditing(s) {
    if (s.dialogFields.some(isFormField))
        return true;
    if (isTypedField(s.active))
        return true;
    return s.touched.some((t) => t.isConnected !== false && isTypedField(t) && (t.value ?? '') !== '');
}
/** No build check sooner than this after the page loaded. */
export const FIRST_CHECK_AFTER_MS = 60_000;
/** How often to check the build while the tab is visible. */
export const CHECK_EVERY_MS = 5 * 60_000;
/** A hashed build file: what Vite writes under `/assets/`. */
const HASHED = /^\/assets\/[^"'?#\s]+\.js$/;
/** The module scripts an HTML page names, in order. */
export function moduleScripts(html) {
    const out = [];
    for (const m of html.matchAll(/<script\b[^>]*>/gi)) {
        const t = m[0];
        if (!/\btype\s*=\s*["']?module/i.test(t))
            continue;
        const src = /\bsrc\s*=\s*["']([^"']+)["']/i.exec(t);
        if (src?.[1])
            out.push(src[1]);
    }
    return out;
}
/** The hashed build scripts in a list, as paths. A dev server's `/src/main.tsx` is not a build. */
export function buildScripts(srcs) {
    return srcs
        .map((s) => s.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]+/i, '').replace(/[?#].*$/, ''))
        .filter((p) => HASHED.test(p));
}
/** Whether the server is handing out a different build from the one running. Not knowing is not newer. */
export function isNewerBuild(running, served) {
    const a = buildScripts(running);
    const b = buildScripts(served);
    if (a.length === 0 || b.length === 0)
        return false;
    return a.slice().sort().join('\n') !== b.slice().sort().join('\n');
}
/** Whether a build check is allowed at this moment. */
export function mayCheck(now, loadedAt, visible) {
    return visible && now - loadedAt >= FIRST_CHECK_AFTER_MS;
}
//# sourceMappingURL=refresh.js.map